import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helpText?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helpText,
  startIcon,
  endIcon,
  fullWidth = true,
  className,
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const hasError = !!error;

  const inputClasses = clsx(
    'block px-3 py-2 border rounded-md shadow-sm placeholder-gray-400',
    'focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors',
    'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
    startIcon && 'pl-10',
    endIcon && 'pr-10',
    fullWidth ? 'w-full' : 'w-auto',
    hasError 
      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 text-gray-900 focus:ring-primary-500 focus:border-primary-500',
    className
  );

  return (
    <div className={fullWidth ? 'w-full' : 'inline-block'}>
      {label && (
        <label 
          htmlFor={inputId}
          className={clsx(
            'block text-sm font-medium mb-1',
            hasError ? 'text-red-700' : 'text-gray-700'
          )}
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        {startIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className={clsx(
              'text-sm',
              hasError ? 'text-red-500' : 'text-gray-400'
            )}>
              {startIcon}
            </span>
          </div>
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          aria-invalid={hasError}
          aria-describedby={
            error ? `${inputId}-error` : 
            helpText ? `${inputId}-help` : 
            undefined
          }
          {...props}
        />
        
        {endIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <span className={clsx(
              'text-sm',
              hasError ? 'text-red-500' : 'text-gray-400'
            )}>
              {endIcon}
            </span>
          </div>
        )}
      </div>
      
      {error && (
        <p 
          id={`${inputId}-error`}
          className="mt-1 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
      
      {helpText && !error && (
        <p 
          id={`${inputId}-help`}
          className="mt-1 text-sm text-gray-500"
        >
          {helpText}
        </p>
      )}
    </div>
  );
});